<template>
  <div class="friends-page">
    <!-- Modern Header -->
    <header class="friends-header">
      <div class="friends-header__left">
        <button class="friends-header__back" @click="$router.back()">
          <i class="bi bi-arrow-right"></i>
        </button>
        <div class="friends-header__title-group">
          <h1 class="friends-header__title">
            {{ $t("chat.friends_list") || "Friends" }}
          </h1>
          <span v-if="!loading && friends.length > 0" class="friends-header__count">
            {{ friends.length }} {{ friends.length === 1 ? ($t("chat.friend") || "friend") : ($t("chat.friends") || "friends") }}
          </span>
        </div>
      </div>
    </header>

    <!-- Search Bar -->
    <div v-if="!loading && friends.length > 0" class="friends-search">
      <div class="friends-search__input-wrapper">
        <i class="bi bi-search friends-search__icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="friends-search__input"
          :placeholder="$t('chat.search_friends') || 'Search friends...'"
        />
        <button
          v-if="searchQuery"
          class="friends-search__clear"
          @click="searchQuery = ''"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="friends-loading">
      <div class="friends-loading__spinner"></div>
      <span class="friends-loading__text">{{ $t("common.loading") || "Loading..." }}</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="friends-error">
      <div class="friends-error__icon">
        <i class="bi bi-exclamation-triangle"></i>
      </div>
      <h3 class="friends-error__title">{{ $t("common.error") || "Something went wrong" }}</h3>
      <p class="friends-error__text">{{ error }}</p>
      <button class="friends-error__btn" @click="loadFriends">
        <i class="bi bi-arrow-clockwise me-2"></i>
        {{ $t("common.retry") || "Try Again" }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="friends.length === 0" class="friends-empty">
      <div class="friends-empty__illustration">
        <div class="friends-empty__circle friends-empty__circle--1"></div>
        <div class="friends-empty__circle friends-empty__circle--2"></div>
        <div class="friends-empty__circle friends-empty__circle--3"></div>
        <i class="bi bi-people friends-empty__icon"></i>
      </div>
      <h3 class="friends-empty__title">{{ $t("chat.no_friends") || "No friends yet" }}</h3>
      <p class="friends-empty__text">
        {{ $t("chat.no_friends_desc") || "Your friends will appear here once you connect with them" }}
      </p>
    </div>

    <!-- No Search Results -->
    <div v-else-if="filteredFriends.length === 0 && searchQuery" class="friends-empty">
      <div class="friends-empty__illustration">
        <i class="bi bi-search friends-empty__icon"></i>
      </div>
      <h3 class="friends-empty__title">{{ $t("chat.no_results") || "No results found" }}</h3>
      <p class="friends-empty__text">
        {{ $t("chat.try_different_search") || "Try a different search term" }}
      </p>
    </div>

    <!-- Friends Grid -->
    <div v-else class="friends-grid">
      <TransitionGroup name="friend-card">
        <div
          v-for="friend in filteredFriends"
          :key="getConversationId(friend) || friend.id"
          class="friend-card"
          :class="{ 'friend-card--removing': removingId === getConversationId(friend) }"
        >
          <!-- Avatar with Status -->
          <div class="friend-card__avatar-wrapper">
            <div class="friend-card__avatar">
              <img
                v-if="friend.image || friend.photo_url || friend.avatar"
                :src="friend.image || friend.photo_url || friend.avatar"
                @error="onAvatarError"
                alt=""
              />
              <div v-else class="friend-card__avatar-placeholder" :style="{ background: getAvatarGradient(friend.name) }">
                {{ getInitials(friend.name) }}
              </div>
            </div>
            <div class="friend-card__status" :class="friend.is_online ? 'friend-card__status--online' : 'friend-card__status--offline'"></div>
          </div>

          <!-- Info -->
          <div class="friend-card__info">
            <h4 class="friend-card__name">{{ friend.name }}</h4>
            <p v-if="friend.email" class="friend-card__email">{{ friend.email }}</p>
            <p v-else class="friend-card__email friend-card__email--placeholder">
              {{ friend.is_online ? ($t("chat.online") || "Online") : ($t("chat.offline") || "Offline") }}
            </p>
          </div>

          <!-- Actions -->
          <div class="friend-card__actions">
            <button
              class="friend-card__action friend-card__action--message"
              @click="openChat(friend)"
              :title="$t('chat.send_message') || 'Send Message'"
            >
              <i class="bi bi-chat-dots"></i>
            </button>
            <div class="friend-card__menu">
              <button
                class="friend-card__action friend-card__action--more"
                @click.stop="toggleMenu(friend, $event)"
                :title="$t('common.more') || 'More'"
              >
                <i class="bi bi-three-dots-vertical"></i>
              </button>
            </div>
          </div>
        </div>
      </TransitionGroup>
      
      <!-- Dropdown Menu (rendered outside cards for proper positioning) -->
      <Teleport to="body">
        <Transition name="menu-fade">
          <div
            v-if="activeMenuId"
            class="friend-card__dropdown"
            :style="dropdownStyle"
            @click.stop
          >
            <button class="friend-card__dropdown-item" @click="openChatFromMenu">
              <i class="bi bi-chat-dots"></i>
              <span>{{ $t("chat.send_message") || "Send Message" }}</span>
            </button>
            <div class="friend-card__dropdown-divider"></div>
            <button class="friend-card__dropdown-item friend-card__dropdown-item--danger" @click="showRemoveDialogFromMenu">
              <i class="bi bi-person-x"></i>
              <span>{{ $t("chat.remove_friend") || "Remove Friend" }}</span>
            </button>
            <button class="friend-card__dropdown-item friend-card__dropdown-item--danger" @click="showBlockDialogFromMenu">
              <i class="bi bi-slash-circle"></i>
              <span>{{ $t("chat.block_user") || "Block User" }}</span>
            </button>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";
import Swal from "sweetalert2";
import { getSwalConfig } from "@/utils/swal-config";

export default {
  name: "FriendsList",
  data() {
    return {
      friends: [],
      loading: true,
      error: null,
      searchQuery: "",
      activeMenuId: null,
      removingId: null,
      dropdownStyle: {},
    };
  },
  computed: {
    filteredFriends() {
      if (!this.searchQuery.trim()) return this.friends;
      const query = this.searchQuery.toLowerCase().trim();
      return this.friends.filter(
        (f) =>
          (f.name && f.name.toLowerCase().includes(query)) ||
          (f.email && f.email.toLowerCase().includes(query))
      );
    },
  },
  mounted() {
    this.loadFriends();
    // Add global click listener to close menu when clicking outside
    document.addEventListener('click', this.handleGlobalClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
  },
  methods: {
    handleGlobalClick(event) {
      // Close menu if clicking outside of any menu
      if (this.activeMenuId) {
        const menuElements = this.$el.querySelectorAll('.friend-card__menu');
        let clickedInsideMenu = false;
        menuElements.forEach(menu => {
          if (menu.contains(event.target)) {
            clickedInsideMenu = true;
          }
        });
        if (!clickedInsideMenu) {
          this.activeMenuId = null;
        }
      }
    },
    async loadFriends() {
      this.loading = true;
      this.error = null;
      try {
        this.friends = await ChatService.getFriendsList();
      } catch (e) {
        console.error("[FriendsList] Failed to load friends:", e);
        this.error = this.$t("chat.failed_to_load_friends") || "Failed to load friends";
      } finally {
        this.loading = false;
      }
    },
    getConversationId(friend) {
      return friend.conversation_id || friend.conversationId || friend.id;
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    },
    getAvatarGradient(name) {
      const gradients = [
        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
        "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
        "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
        "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
        "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
      ];
      const index = name ? name.charCodeAt(0) % gradients.length : 0;
      return gradients[index];
    },
    onAvatarError(e) {
      // Set default avatar image
      e.target.src = "/assets/img/acad-icon.png";
    },
    toggleMenu(friend, event) {
      const id = this.getConversationId(friend);
      if (this.activeMenuId === id) {
        this.activeMenuId = null;
        this.dropdownStyle = {};
      } else {
        this.activeMenuId = id;
        // Calculate position based on button location
        this.$nextTick(() => {
          const btn = event.currentTarget;
          const rect = btn.getBoundingClientRect();
          const dropdownHeight = 160; // Approximate height
          const dropdownWidth = 220;
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          
          // Calculate position - show above if not enough space below
          let top = rect.bottom + 8;
          let left = rect.right - dropdownWidth;
          
          // If dropdown would go below viewport, show above
          if (top + dropdownHeight > viewportHeight - 20) {
            top = rect.top - dropdownHeight - 8;
          }
          
          // If dropdown would go off left edge, align to left
          if (left < 10) {
            left = 10;
          }
          
          // If dropdown would go off right edge
          if (left + dropdownWidth > viewportWidth - 10) {
            left = viewportWidth - dropdownWidth - 10;
          }
          
          this.dropdownStyle = {
            top: `${top}px`,
            left: `${left}px`,
          };
        });
      }
    },
    closeMenu() {
      this.activeMenuId = null;
      this.dropdownStyle = {};
    },
    getActiveFriend() {
      if (!this.activeMenuId) return null;
      return this.friends.find(f => this.getConversationId(f) === this.activeMenuId);
    },
    openChat(friend) {
      this.closeMenu();
      const convId = this.getConversationId(friend);
      if (convId) {
        this.$router.push({ name: "ChatConversation", params: { conversationId: convId } });
      }
    },
    openChatFromMenu() {
      const friend = this.getActiveFriend();
      if (friend) this.openChat(friend);
    },
    async showRemoveDialog(friend) {
      this.closeMenu();
      const convId = this.getConversationId(friend);
      if (!friend || !convId) return;

      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.remove_friend") || "Remove Friend",
        html: `
          <p style="margin-bottom: 8px; color: #374151;">${this.$t("chat.remove_friend_confirmation_name", { name: friend.name }) || `Are you sure you want to remove <strong>${friend.name}</strong>?`}</p>
          <p style="font-size: 14px; color: #6b7280;">${this.$t("chat.remove_friend_warning") || "You will no longer be able to message each other."}</p>
        `,
        showCancelButton: true,
        confirmButtonText: this.$t("chat.remove") || "Remove",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
        focusCancel: true,
      });

      if (result.isConfirmed) {
        await this.executeRemoveFriend(friend, convId);
      }
    },
    showRemoveDialogFromMenu() {
      const friend = this.getActiveFriend();
      if (friend) this.showRemoveDialog(friend);
    },
    async executeRemoveFriend(friend, convId) {
      // Show loading
      Swal.fire({
        ...getSwalConfig("default"),
        title: this.$t("common.please_wait") || "Please wait...",
        text: this.$t("chat.removing_friend") || "Removing friend...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const result = await ChatService.removeFriend(convId);

        if (result.success !== false) {
          this.removingId = convId;
          
          setTimeout(() => {
            this.friends = this.friends.filter((f) => this.getConversationId(f) !== convId);
            this.removingId = null;
          }, 300);

          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.success") || "Success",
            text: this.$t("chat.friend_removed_successfully") || "Friend removed successfully",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title: this.$t("common.error") || "Error",
            text: result.message || this.$t("chat.failed_to_remove_friend") || "Failed to remove friend",
          });
        }
      } catch (e) {
        console.error("[FriendsList] Error removing friend:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("common.error") || "Error",
          text: this.$t("chat.failed_to_remove_friend") || "Failed to remove friend",
        });
      }
    },
    async showBlockDialog(friend) {
      this.closeMenu();
      const convId = this.getConversationId(friend);
      if (!friend || !convId) return;

      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.block_user") || "Block User",
        html: `
          <p style="margin-bottom: 8px; color: #374151;">${this.$t("chat.block_user_confirmation_name", { name: friend.name }) || `Are you sure you want to block <strong>${friend.name}</strong>?`}</p>
          <p style="font-size: 14px; color: #6b7280;">${this.$t("chat.block_user_warning") || "They won't be able to send you messages or see your online status."}</p>
        `,
        showCancelButton: true,
        confirmButtonText: this.$t("chat.block") || "Block",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        reverseButtons: true,
        focusCancel: true,
      });

      if (result.isConfirmed) {
        await this.executeBlockUser(friend, convId);
      }
    },
    showBlockDialogFromMenu() {
      const friend = this.getActiveFriend();
      if (friend) this.showBlockDialog(friend);
    },
    async executeBlockUser(friend, convId) {
      // Show loading
      Swal.fire({
        ...getSwalConfig("default"),
        title: this.$t("common.please_wait") || "Please wait...",
        text: this.$t("chat.blocking_user") || "Blocking user...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const result = await ChatService.blockUser(convId);

        if (result.success !== false) {
          this.removingId = convId;

          setTimeout(() => {
            this.friends = this.friends.filter((f) => this.getConversationId(f) !== convId);
            this.removingId = null;
          }, 300);

          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.success") || "Success",
            text: this.$t("chat.user_blocked_successfully") || "User blocked successfully",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title: this.$t("common.error") || "Error",
            text: result.message || this.$t("chat.failed_to_block_user") || "Failed to block user",
          });
        }
      } catch (e) {
        console.error("[FriendsList] Error blocking user:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("common.error") || "Error",
          text: this.$t("chat.failed_to_block_user") || "Failed to block user",
        });
      }
    },
  },
};
</script>

<style scoped>
/* ========== Page Layout ========== */
.friends-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--bs-body-bg) 0%, #f8fafc 100%);
  overflow-x: hidden;
  position: relative;
}

/* ========== Header ========== */
.friends-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: var(--bs-card-bg);
  border-bottom: 1px solid var(--bs-border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
}

.friends-header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.friends-header__back {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.1rem;
}

.friends-header__back i,
.friends-header__back::before {
  color: #ffffff !important;
}

.friends-header__back:hover {
  transform: translateX(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.friends-header__title-group {
  display: flex;
  flex-direction: column;
}

.friends-header__title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
}

.friends-header__count {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

/* ========== Search ========== */
.friends-search {
  padding: 0.75rem 1.25rem;
  background: var(--bs-card-bg);
  border-bottom: 1px solid var(--bs-border-color);
}

.friends-search__input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.friends-search__icon {
  position: absolute;
  left: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
  pointer-events: none;
}

.friends-search__input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.75rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background: var(--bs-body-bg);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  color: #1f2937;
}

.friends-search__input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.friends-search__input::placeholder {
  color: #9ca3af;
}

.friends-search__clear {
  position: absolute;
  right: 0.75rem;
  width: 24px;
  height: 24px;
  border: none;
  background: #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4b5563;
  font-size: 0.7rem;
  transition: all 0.2s ease;
}

.friends-search__clear:hover {
  background: #9ca3af;
}

/* ========== Loading State ========== */
.friends-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  gap: 1rem;
}

.friends-loading__spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--bs-border-color);
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.friends-loading__text {
  color: #6b7280;
  font-size: 0.95rem;
}

/* ========== Error State ========== */
.friends-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.friends-error__icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.friends-error__icon i {
  font-size: 2rem;
  color: #ef4444 !important;
}

.friends-error__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.friends-error__text {
  color: #6b7280;
  margin-bottom: 1.5rem;
  max-width: 300px;
}

.friends-error__btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff !important;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.friends-error__btn i {
  color: #ffffff !important;
}

.friends-error__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* ========== Empty State ========== */
.friends-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.friends-empty__illustration {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.friends-empty__circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
}

.friends-empty__circle--1 {
  width: 120px;
  height: 120px;
  background: #10b981;
  animation: pulse 2s ease-in-out infinite;
}

.friends-empty__circle--2 {
  width: 90px;
  height: 90px;
  background: #10b981;
  animation: pulse 2s ease-in-out infinite 0.3s;
}

.friends-empty__circle--3 {
  width: 60px;
  height: 60px;
  background: #10b981;
  animation: pulse 2s ease-in-out infinite 0.6s;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.1; }
  50% { transform: scale(1.05); opacity: 0.2; }
}

.friends-empty__icon {
  font-size: 2.5rem;
  color: #10b981;
  position: relative;
  z-index: 1;
}

.friends-empty__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.friends-empty__text {
  color: #6b7280;
  max-width: 280px;
  line-height: 1.5;
}

/* ========== Friends Grid ========== */
.friends-grid {
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
  overflow: visible;
}

/* ========== Friend Card ========== */
.friend-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bs-card-bg);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
}

.friend-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.friend-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
}

.friend-card:hover::before {
  opacity: 1;
}

.friend-card--removing {
  opacity: 0;
  transform: translateX(100px);
}

/* Avatar */
.friend-card__avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.friend-card__avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  overflow: hidden;
  background: var(--bs-gray-200);
}

.friend-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.friend-card__avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;
}

.friend-card__status {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid var(--bs-card-bg);
}

.friend-card__status--online {
  background: #10b981;
}

.friend-card__status--offline {
  background: var(--bs-gray-400);
}

/* Info */
.friend-card__info {
  flex: 1;
  min-width: 0;
}

.friend-card__name {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.friend-card__email {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.friend-card__email--placeholder {
  font-style: italic;
  color: #9ca3af;
}

/* Actions */
.friend-card__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 10;
}

.friend-card__action {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.1rem;
}

.friend-card__action--message {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff !important;
}

.friend-card__action--message i,
.friend-card__action--message::before {
  color: #ffffff !important;
}

.friend-card__action--message:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.friend-card__action--more {
  background: #f3f4f6;
  color: #6b7280;
}

.friend-card__action--more:hover {
  background: #e5e7eb;
  color: #374151;
}

/* Dropdown Menu */
.friend-card__menu {
  position: static;
}

.friend-card__dropdown {
  position: fixed;
  min-width: 200px;
  max-width: 240px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);
  padding: 6px;
  z-index: 9999;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transform-origin: top right;
}

.friend-card__dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #1f2937;
  transition: all 0.15s ease;
  text-align: right;
  font-weight: 500;
  white-space: nowrap;
}

.friend-card__dropdown-item:hover {
  background: #f3f4f6;
}

.friend-card__dropdown-item--danger {
  color: #dc2626;
}

.friend-card__dropdown-item--danger:hover {
  background: #fef2f2;
  color: #b91c1c;
}

.friend-card__dropdown-item i {
  font-size: 16px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.friend-card__dropdown-item--danger i {
  color: inherit !important;
}

.friend-card__dropdown-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

/* ========== Transitions ========== */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.2s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.friend-card-enter-active,
.friend-card-leave-active {
  transition: all 0.3s ease;
}

.friend-card-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.friend-card-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

/* ========== Responsive ========== */
@media (max-width: 480px) {
  .friends-header {
    padding: 0.875rem 1rem;
  }

  .friends-header__back {
    width: 36px;
    height: 36px;
  }

  .friends-header__title {
    font-size: 1.1rem;
  }

  .friends-search {
    padding: 0.5rem 1rem;
  }

  .friends-grid {
    padding: 0.75rem;
  }

  .friend-card {
    padding: 0.875rem;
  }

  .friend-card__avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .friend-card__status {
    width: 14px;
    height: 14px;
  }

  .friend-card__name {
    font-size: 0.95rem;
  }

  .friend-card__action {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
}

/* ========== Dark Mode Support ========== */
[data-bs-theme="dark"] .friends-page {
  background: linear-gradient(180deg, var(--bs-body-bg) 0%, #0f172a 100%);
}

[data-bs-theme="dark"] .friends-header__title {
  color: #f9fafb;
}

[data-bs-theme="dark"] .friends-header__count {
  color: #9ca3af;
}

[data-bs-theme="dark"] .friends-search__input {
  color: #f9fafb;
  background: #1f2937;
  border-color: #374151;
}

[data-bs-theme="dark"] .friends-search__icon {
  color: #9ca3af;
}

[data-bs-theme="dark"] .friends-loading__text {
  color: #9ca3af;
}

[data-bs-theme="dark"] .friends-error__title,
[data-bs-theme="dark"] .friends-empty__title {
  color: #f9fafb;
}

[data-bs-theme="dark"] .friends-error__text,
[data-bs-theme="dark"] .friends-empty__text {
  color: #9ca3af;
}

[data-bs-theme="dark"] .friend-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .friend-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .friend-card__name {
  color: #f9fafb;
}

[data-bs-theme="dark"] .friend-card__email {
  color: #9ca3af;
}

[data-bs-theme="dark"] .friend-card__action--more {
  background: #374151;
  color: #d1d5db;
}

[data-bs-theme="dark"] .friend-card__action--more:hover {
  background: #4b5563;
  color: #f9fafb;
}

[data-bs-theme="dark"] .friend-card__dropdown {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .friend-card__dropdown-item {
  color: #e5e7eb;
}

[data-bs-theme="dark"] .friend-card__dropdown-item:hover {
  background: #374151;
}

[data-bs-theme="dark"] .friend-card__dropdown-item--danger {
  color: #f87171;
}

[data-bs-theme="dark"] .friend-card__dropdown-item--danger:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
}

[data-bs-theme="dark"] .friend-card__dropdown-divider {
  background: #374151;
}
</style>

<!-- Global styles for Teleported dropdown -->
<style>
.friend-card__dropdown {
  position: fixed;
  min-width: 200px;
  max-width: 240px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 9999;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.friend-card__dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #1f2937;
  transition: all 0.15s ease;
  text-align: right;
  font-weight: 500;
  white-space: nowrap;
}

.friend-card__dropdown-item:hover {
  background: #f3f4f6;
}

.friend-card__dropdown-item--danger {
  color: #dc2626;
}

.friend-card__dropdown-item--danger:hover {
  background: #fef2f2;
  color: #b91c1c;
}

.friend-card__dropdown-item i {
  font-size: 18px;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
  color: inherit !important;
}

.friend-card__dropdown-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 6px 0;
}

/* Dark mode for dropdown */
[data-bs-theme="dark"] .friend-card__dropdown {
  background: #1f2937;
  border-color: #374151;
}

[data-bs-theme="dark"] .friend-card__dropdown-item {
  color: #e5e7eb;
}

[data-bs-theme="dark"] .friend-card__dropdown-item:hover {
  background: #374151;
}

[data-bs-theme="dark"] .friend-card__dropdown-item--danger {
  color: #f87171;
}

[data-bs-theme="dark"] .friend-card__dropdown-item--danger:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
}

[data-bs-theme="dark"] .friend-card__dropdown-divider {
  background: #374151;
}

/* Menu fade transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.2s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
